import isEmpty from 'lodash-es/isEmpty'

/*
This function is to prevent undefined values from being processed and resolves
the keys configuration into a standardized format with singleValueKeys and multiValueKeys
*/
const resolveKeys = (keys: string[] | { singleValueKeys?: string[]; multiValueKeys?: string[] }) => {
    const singleValueKeys = Array.isArray(keys) ? keys : ('singleValueKeys' in keys ? keys.singleValueKeys : []) || []

    const multiValueKeys = ('multiValueKeys' in keys ? keys.multiValueKeys : []) || []

    return { singleValueKeys, multiValueKeys }
}

export function parseFiltersFromQuery(
    query: any,
    keys: string[] | { singleValueKeys?: string[]; multiValueKeys?: string[] }
): Record<string, string | string[]> {
    const { singleValueKeys, multiValueKeys } = resolveKeys(keys)

    const filters = [...singleValueKeys, ...multiValueKeys].reduce((acc, key) => {
        if (!query[key]) {
            return acc
        }

        // Decide whether to handle value a scalar or an array
        let value
        if (multiValueKeys.includes(key)) {
            // Handle both string (comma-separated) and array (from Vue Router repeated params)
            const queryValue = query[key]
            if (Array.isArray(queryValue)) {
                value = queryValue.map(decodeURIComponent)
            } else {
                value = queryValue.split(',').map(decodeURIComponent)
            }
        } else {
            value = query[key]
        }

        // Convert 'true' and 'false' string values to boolean
        if (value === 'true') {
            value = true
        }

        if (value === 'false') {
            value = false
        }

        return { ...acc, [key]: value }
    }, {})

    return filters
}

/*
    singleValueKeys: Array of keys that should be treated as single values in the query string
    multiValueKeys: Array of keys that should be treated as comma-separated arrays in the query string
*/
export function serializeFiltersIntoQuery(
    filters: any,
    keys: string[] | { singleValueKeys?: string[]; multiValueKeys?: string[] }
): Record<string, string> {
    const { singleValueKeys, multiValueKeys } = resolveKeys(keys)

    const serializedFilters = [...singleValueKeys, ...multiValueKeys].reduce((acc, key) => {
        if (!filters[key] || (typeof filters[key] === 'object' && isEmpty(filters[key]))) {
            return acc
        }

        const value = multiValueKeys.includes(key) ? filters[key].map(encodeURIComponent).join(',') : filters[key]

        return { ...acc, [key]: value }
    }, {})

    return serializedFilters
}

export function appendValueToQueryParam(query: any, paramName: string, newValue: string): Record<string, string> {
    const { [paramName]: currentValue } = parseFiltersFromQuery(query, {
        multiValueKeys: [paramName]
    })

    if (currentValue?.includes(newValue)) {
        return query
    }

    const queryUpdate = serializeFiltersIntoQuery(
        { [paramName]: [...(currentValue || []), newValue] },
        { multiValueKeys: [paramName] }
    )

    return {
        ...query,
        ...queryUpdate
    }
}
